import { AnswerSourceFilter } from 'services/streamInsightsQuery';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';

import { answerSourceFilterToPlate } from './answerSourceFilterToFixedFilter';
import { toClause } from './buildReportInsightsView';

// Identity of a drill-down for dedup, via the same clause mapping the forward
// direction uses — so an agent filter that resolves to a plate already on screen
// isn't added twice. Returns null for a plate with no clauses (never equal).
const clauseKey = (fixed: FixedFilter): string | null => {
  const clauses = toClause(fixed);
  return clauses.length ? JSON.stringify(clauses) : null;
};

// Carries an agent-applied filter set through to a report's drill-downs:
// transient, accumulates (AND) onto whatever's already applied, skips fields the
// report can't filter (not in fieldByName) or that are red-listed, and skips exact
// duplicates. Returns `prev` unchanged when nothing is added, so the caller avoids
// a needless state write / re-publish.
const mergeAgentFiltersIntoDrillDowns = ({
  prev,
  filters,
  fieldByName,
  fieldRedList,
}: {
  prev: FilterPlateType[];
  filters: AnswerSourceFilter[];
  fieldByName: Map<string, FleetOps.Field>;
  fieldRedList?: string[];
}): FilterPlateType[] => {
  const seen = new Set(
    prev
      .filter(filterPlateTypeCheckers.isFixed)
      .map((plate) => clauseKey(plate.fixedValue))
      .filter((key): key is string => key !== null),
  );

  const additions: FilterPlateType[] = [];
  filters.forEach((filter) => {
    const fieldDef = fieldByName.get(filter.field);
    if (!fieldDef || fieldRedList?.includes(filter.field)) {
      return;
    }
    const plate = answerSourceFilterToPlate(filter, fieldDef);
    if (!plate || !filterPlateTypeCheckers.isFixed(plate)) {
      return;
    }
    const key = clauseKey(plate.fixedValue);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    additions.push(plate);
  });

  return additions.length ? [...prev, ...additions] : prev;
};

export default mergeAgentFiltersIntoDrillDowns;
